import {type Key, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useMediaQuery} from 'usehooks-ts';
import noop from '@tinkoff/utils/function/noop';
import isEqual from '@tinkoff/utils/is/equal';
import {FixedSizeList as List} from 'react-window';

import {FormattedMessage, useIntl} from 'react-intl';

import type {Dictionary} from '@reduxjs/toolkit';

import classNames from 'classnames';

import {ModalDialog, ModalSize} from '@time-webkit/all/organisms/modal-dialog';

import {DownloadOutlineIcon} from '@time-webkit/all/icons/download-outline';

import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';

import {ActionButton} from '@time-webkit/all/molecules/icon-button';

import {shouldShowMilitaryTime} from 'mattermost-redux/selectors/entities/preferences';
import {getCurrentTeamName} from 'mattermost-redux/selectors/entities/teams';
import {getReactionsTableSrc, makeGetUniqueReactionsToPost} from 'utils/post_utils';
import {makeGetProfilesForReactions} from 'mattermost-redux/selectors/entities/users';
import {type User} from 'features/users';

import {useAppDispatch, useAppSelector} from 'stores/redux_store';

import {fetchUsersByIdsDebounced} from 'features/users/actions/fetch_users_by_ids';

import {browserHistory} from 'utils/browser_history';

import styles from './styles.module.css';
import {ReactionListItem} from './reaction_list_item';
import {ReactionTabs} from './reaction_tabs';

const LIST_HEIGHT = 352;

interface ReactionsModalProps {
    postId: string;
    onExited: () => void;
    defaultSelected?: string;
}
const getReactionsForPost = makeGetUniqueReactionsToPost();
const getUsersByReactions = makeGetProfilesForReactions();

function isFullUser(user: User | {id: User['id']}): user is User {
    return 'username' in user;
}

export const ReactionsModal: React.FC<ReactionsModalProps> = ({postId, onExited, defaultSelected = 'all'}) => {
    const dispatch = useAppDispatch();
    const {formatMessage} = useIntl();
    const [opened, setOpened] = useState(true);
    const [selected, setSelected] = useState<Key>(defaultSelected);
    const reactions = useAppSelector((state) => {
        return Object.values(getReactionsForPost(state, postId) || {})
            .filter((reaction) => selected === 'all' || reaction.emoji_name === selected)
            .sort((a, b) => b.create_at - a.create_at);
    }, isEqual);
    const users = useAppSelector((state) => {
        const usersFromReactions = getUsersByReactions(state, reactions);
        const userIdsFromReactions = Array.from(new Set(reactions.map((reaction) => reaction.user_id)));

        return userIdsFromReactions.reduce((result, userId) => {
            const user = usersFromReactions.find((user) => user.id === userId);
            if (!user) {
                return {
                    ...result,
                    [userId]: {
                        id: userId,
                    },
                };
            }

            return {
                ...result,
                [userId]: user,
            };
        }, {} as Dictionary<User | {id: User['id']}>);
    }, isEqual);
    const isMilitaryTime = useAppSelector((state) => shouldShowMilitaryTime(state), isEqual);

    const listContainerRef = useRef<HTMLDivElement | null>(null);
    const currentTeamName = useAppSelector(getCurrentTeamName);
    const isMobile = useMediaQuery('(max-width: 575px)');

    const handleSelectChange = (name: Key) => {
        setSelected(name);
    };

    const onHide = useCallback(() => {
        setOpened(false);
        onExited();
    }, [onExited]);

    const jumpToDirect = useCallback(
        (username: string) => {
            browserHistory.push(`/${currentTeamName}/messages/@${username}`);
            onHide();
        },
        [currentTeamName, onHide],
    );

    const usersToLoad = useMemo(() => {
        return Object.values(users)
            .filter((user) => {
                if (!user) {
                    return true;
                }

                if (isFullUser(user)) {
                    return false;
                }

                return true;
            })
            .map((user) => user!.id);
    }, [users]);

    useEffect(() => {
        if (usersToLoad.length) {
            dispatch(fetchUsersByIdsDebounced(usersToLoad));
        }
    }, [usersToLoad, dispatch]);

    const headingCls = useTypography({
        size: TypographySize.BodyLTrue,
        noDefaultColor: true,
    });

    if (!opened) {
        return null;
    }

    return (
        <ModalDialog
            isOpen={true}
            onClose={onHide}
            onConfirm={noop}
            size={ModalSize.MEDIUM}
            title={
                <div className={styles.title}>
                    <FormattedMessage
                        id='post_info.reactions'
                        defaultMessage='See reactions'
                    />
                    <ActionButton
                        as='a'
                        href={getReactionsTableSrc(postId)}
                        rel='noopener noreferrer'
                        download='true'
                        className={styles.download}
                    >
                        <DownloadOutlineIcon
                            className={styles.downloadIcon}
                            size={16}
                        />
                        <span
                            className={classNames(
                                headingCls,
                                styles.downloadText,
                            )}
                        >
                            {formatMessage({
                                id: 'post_info.reactions.results',
                                defaultMessage: 'Results',
                            })}
                        </span>
                    </ActionButton>
                </div>
            }
        >
            <div className={styles.tabs}>
                <ReactionTabs
                    postId={postId}
                    selectedKey={selected}
                    onSelectionChange={handleSelectChange}
                    isMobile={isMobile}
                />
            </div>

            <div className={styles.list__scrolled} ref={listContainerRef}>
                <List
                    itemCount={reactions.length}
                    className={styles.users__list}
                    itemSize={52}
                    height={LIST_HEIGHT}
                    width='100%'
                >
                    {({index, style}) => {
                        const reaction = reactions[index]!;
                        return (
                            <div
                                key={reaction.emoji_name + reaction.user_id}
                                style={style}
                            >
                                <ReactionListItem
                                    reaction={reaction}
                                    userId={reaction.user_id}
                                    isMobile={isMobile}
                                    isMilitaryTime={isMilitaryTime}
                                    jumpToDirect={jumpToDirect}
                                />
                            </div>
                        );
                    }}
                </List>
            </div>
        </ModalDialog>
    );
};
